import React, { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { Settings, LogOut, ChevronDown, Star } from 'lucide-react'

export const KidAccountMenu: React.FC = () => {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleLogout = () => {
    sessionStorage.removeItem('oauth_token_processed')
    logout()
    navigate('/login')
  }

  const getInitials = (name?: string) => {
    if (!name) return 'K'
    // Filter out words starting with non-letters (like parentheses)
    return name.split(' ')
      .filter(n => n.length > 0 && /^[a-zA-Z]/.test(n))
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) || name.charAt(0).toUpperCase()
  }

  const userColor = user?.color_theme || '#6366f1'

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
      >
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium"
          style={{ backgroundColor: userColor }}
        >
          {getInitials(user?.display_name)}
        </div>
        <div className="hidden sm:block text-left">
          <div className="text-sm font-medium text-gray-900">{user?.display_name}</div>
          <div className="text-xs text-gray-500 flex items-center gap-1">
            <Star className="w-3 h-3" style={{ color: userColor }} />
            {(user?.available_points ?? user?.total_points ?? 0).toLocaleString()} avail
          </div>
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50">
          {/* User Info Header */}
          <div className="px-4 py-3 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center text-white font-medium"
                style={{ backgroundColor: userColor }}
              >
                {getInitials(user?.display_name)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-900 truncate">{user?.display_name}</div>
                <div className="text-sm flex items-center gap-1" style={{ color: userColor }}>
                  <Star className="w-3.5 h-3.5" />
                  <span className="font-semibold">{(user?.available_points ?? user?.total_points ?? 0).toLocaleString()}</span>
                  <span className="text-gray-500">available</span>
                </div>
                <div className="text-xs text-gray-400">
                  {(user?.lifetime_points_earned ?? 0).toLocaleString()} lifetime earned
                </div>
              </div>
            </div>
          </div>

          {/* Menu Items */}
          <div className="py-1">
            <button
              onClick={() => {
                setIsOpen(false)
                navigate('/dashboard/settings')
              }}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <Settings className="w-4 h-4 text-gray-400" />
              Settings
            </button>
          </div>

          {/* Logout */}
          <div className="border-t border-gray-100 pt-1">
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
